import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { seriesList } from "@/mocks/series";

interface SearchBoxProps {
  variant?: "nav" | "panel";
  onNavigate?: () => void;
}

export default function SearchBox({ variant = "nav", onNavigate }: SearchBoxProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return [];
    return seriesList
      .filter((item) => {
        if (item.title.toLowerCase().includes(keyword)) return true;
        if (item.cast.some((actor) => actor.toLowerCase().includes(keyword))) return true;
        return item.characters.some(
          (char) =>
            char.name.toLowerCase().includes(keyword) ||
            char.actor.toLowerCase().includes(keyword)
        );
      })
      .slice(0, 6);
  }, [query]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const goDetail = (id: string) => {
    setOpen(false);
    setQuery("");
    navigate(`/detail/${id}`);
    onNavigate?.();
  };

  const goCategory = () => {
    if (!query.trim()) return;
    navigate(`/category?q=${encodeURIComponent(query.trim())}`);
    setOpen(false);
    onNavigate?.();
  };

  return (
    <div ref={boxRef} className="relative w-full">
      <div
        className={`flex items-center gap-2 rounded-full border border-background-300/70 bg-background-50/95 px-3 py-2 text-foreground-700 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-200/60 ${
          variant === "panel" ? "w-full" : "w-56 xl:w-64"
        }`}
      >
        <button
          type="button"
          aria-label="搜索"
          onClick={goCategory}
          className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-500 hover:text-primary-600"
        >
          <i className="ri-search-line text-base"></i>
        </button>
        <input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter") goCategory();
          }}
          placeholder="搜索剧集或角色"
          className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
        />
      </div>

      {open && query.trim() && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-lg">
          {results.length > 0 ? (
            <ul className="max-h-80 overflow-y-auto py-1">
              {results.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => goDetail(item.id)}
                    className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left hover:bg-primary-50"
                  >
                    <span className="h-14 w-10 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={item.poster}
                        alt={item.title}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-foreground-900">
                        {item.title}
                      </span>
                      <span className="block truncate text-xs text-foreground-500">
                        {item.year} · {item.category} · {item.characters.map((c) => c.name).join(" / ")}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-5 text-center">
              <p className="text-sm text-foreground-600">没有找到与「{query}」匹配的剧集</p>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  navigate("/category");
                  setOpen(false);
                  onNavigate?.();
                }}
                className="mt-2 cursor-pointer text-xs font-medium text-primary-600 hover:underline"
              >
                去热门剧集看看 →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}